import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Italiana&family=DM+Sans:wght@300;400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
`;

// ── Keyframes
const flicker = keyframes`
  0%, 100% { opacity: 1; }
  8%        { opacity: 0.9; }
  9%        { opacity: 1; }
  42%       { opacity: 1; }
  43%       { opacity: 0.85; }
  44%       { opacity: 1; }
  77%       { opacity: 1; }
  78%       { opacity: 0.92; }
  79%       { opacity: 1; }
`;

const scanDown = keyframes`
  0%   { transform: translateY(-100%); opacity: 0; }
  5%   { opacity: 0.5; }
  95%  { opacity: 0.5; }
  100% { transform: translateY(1200%); opacity: 0; }
`;

const glitchX = keyframes`
  0%, 100% { clip-path: inset(0 0 100% 0); transform: translateX(0); }
  20%       { clip-path: inset(15% 0 70% 0); transform: translateX(-4px); }
  40%       { clip-path: inset(50% 0 30% 0); transform: translateX(4px); }
  60%       { clip-path: inset(80% 0 5% 0); transform: translateX(-2px); }
  80%       { clip-path: inset(30% 0 55% 0); transform: translateX(3px); }
`;

const pulseGlow = keyframes`
  0%, 100% { text-shadow: 0 0 20px rgba(232,121,249,0.3), 0 0 40px rgba(168,85,247,0.15); }
  50%       { text-shadow: 0 0 40px rgba(232,121,249,0.6), 0 0 80px rgba(168,85,247,0.3), 0 0 120px rgba(124,58,237,0.15); }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  49%       { opacity: 1; }
  50%       { opacity: 0; }
  99%       { opacity: 0; }
`;

// ── Wrapper
const Wrapper = styled.div`
  position: relative;
`;

// Each beat is its own sticky panel
const Beat = styled.div`
  position: relative;
  height: ${({ h }) => h || "200vh"};
`;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  background: ${({ bg }) => bg || "#030008"};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.04;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

// Scanline overlay
const ScanLine = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 247, 0.4),
    transparent
  );
  pointer-events: none;
  z-index: 10;
  animation: ${scanDown} 5s linear infinite;
`;

// Horizontal rule that sweeps
const Rule = styled.div`
  position: absolute;
  left: 0;
  height: 1px;
  width: 0%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(168, 85, 247, 0.5),
    transparent
  );
  top: ${({ top }) => top};
  will-change: width;
`;

// Ambient orbs
const Orb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "110px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.2};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  will-change: opacity, transform;
`;

// Big display word
const Word = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: ${({ size }) => size || "clamp(5rem, 16vw, 16rem)"};
  line-height: 0.88;
  letter-spacing: 0.04em;
  color: ${({ c }) => c || "#fff"};
  position: relative;
  z-index: 2;
  will-change: transform, opacity, filter, letter-spacing;
  white-space: nowrap;

  &.stroke {
    -webkit-text-stroke: 1.5px rgba(255, 255, 255, 0.808);
    color: transparent;
  }

  &.glow {
    color: #e879f9;
    animation: ${pulseGlow} 3s ease-in-out infinite;
  }

  &.flicker {
    animation: ${flicker} 6s linear infinite;
  }
  @media (max-width: 500px) {
    font-size: 15vw;
  }
`;

// Glitch ghost layer
const GlitchLayer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  font-family: "Mendl", sans-serif;
  font-size: ${({ size }) => size || "clamp(5rem, 16vw, 16rem)"};
  line-height: 0.88;
  letter-spacing: 0.04em;
  color: #e879f9;
  opacity: 0.5;
  pointer-events: none;
  animation: ${glitchX} 4s steps(1) infinite;
  will-change: transform, clip-path;
  @media (max-width: 500px) {
    font-size: 15vw;
  }
`;

const WordWrap = styled.div`
  position: relative;
  display: inline-block;
  z-index: 2;
`;

// Layout containers
const CenterStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
  z-index: 2;
`;

const LeftStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.15rem;
  z-index: 2;
  width: min(1200px, 90vw);
  padding-left: 5vw;
`;

// Serif whisper line
const Whisper = styled.div`
  font-family: "Mendl", serif;
  font-size: clamp(1.2rem, 2.5vw, 2.2rem);
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.3;
  z-index: 2;
  will-change: transform, opacity;
  font-style: italic;
  text-align: center;
`;

// The small "and I mean - Cybersecurity" line
const CyberLine = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  z-index: 2;
  will-change: transform, opacity;
`;

const CyberDash = styled.div`
  width: 32px;
  height: 1px;
  background: rgba(168, 85, 247, 0.5);
  flex-shrink: 0;
`;

const CyberText = styled.div`
  font-family: "Mendl", sans-serif;
  font-size: clamp(0.85rem, 1.8vw, 1.3rem);
  font-weight: 300;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
`;

const CyberAccent = styled.span`
  color: #c084fc;
  font-weight: 500;
`;

// Corner counter
const Counter = styled.div`
  position: absolute;
  bottom: 2.5rem;
  right: 3rem;
  z-index: 10;
  font-family: "Mendl", sans-serif;
  font-size: 0.65rem;
  color: rgba(255, 255, 255, 0.2);
  letter-spacing: 0.1em;
  display: flex;
  align-items: center;
  gap: 4px;
`;

// Cursor blink
const Cursor = styled.span`
  display: inline-block;
  width: 3px;
  height: 0.8em;
  background: #a855f7;
  margin-left: 4px;
  vertical-align: middle;
  animation: ${blink} 1s step-end infinite;
`;

export default function SecurityBridge() {
  // Beat 1: "NOW LET'S" — crashes in from above
  const b1line1 = useRef(null);
  const b1line2 = useRef(null);
  const b1orb = useRef(null);
  const b1rule = useRef(null);

  // Beat 2: "TALK ABOUT" — slides from sides
  const b2top = useRef(null);
  const b2bot = useRef(null);

  // Beat 3: "SECURITY" — zooms out of blur with glitch
  const b3wrap = useRef(null);
  const b3word = useRef(null);
  const b3sub = useRef(null);
  const b3orb = useRef(null);

  // Beat 4: final — "and I mean - Cybersecurity"
  const b4line1 = useRef(null);
  const b4line2 = useRef(null);
  const b4cyber = useRef(null);
  const b4rule = useRef(null);

  useEffect(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1px)", () => {
      // ── BEAT 1: "NOW LET'S" — top line drops, bottom line rises
      gsap.set(b1line1.current, { y: "-130%", opacity: 0, skewY: -5 });
      gsap.set(b1line2.current, { y: "130%", opacity: 0, skewY: 5 });
      gsap.set(b1orb.current, { opacity: 0, scale: 0.3 });
      gsap.set(b1rule.current, { width: "0%" });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#sb1",
            start: "top top",
            end: "+=80%",
            scrub: 0.65,
          },
        })
        .to(b1orb.current, { opacity: 1, scale: 1, ease: "power2.out" }, 0)
        .to(
          b1line1.current,
          { y: "0%", opacity: 1, skewY: 0, ease: "power4.out" },
          0,
        )
        .to(
          b1line2.current,
          { y: "0%", opacity: 1, skewY: 0, ease: "power4.out" },
          0.06,
        )
        .to(b1rule.current, { width: "100%", ease: "power2.out" }, 0.4);

      // ── BEAT 2: "TALK" crashes left, "ABOUT" crashes right
      gsap.set(b2top.current, { x: "-110vw", opacity: 0 });
      gsap.set(b2bot.current, { x: "110vw", opacity: 0 });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#sb2",
            start: "top top",
            end: "+=80%",
            scrub: 0.7,
          },
        })
        .to(b2top.current, { x: 0, opacity: 1, ease: "power3.out" }, 0)
        .to(b2bot.current, { x: 0, opacity: 1, ease: "power3.out" }, 0.05);

      // ── BEAT 3: "SECURITY" — letterSpacing collapses + zoom from blur
      gsap.set(b3word.current, {
        letterSpacing: "0.5em",
        opacity: 0,
        scale: 1.3,
        filter: "blur(30px)",
      });
      gsap.set(b3sub.current, { opacity: 0, y: 20 });
      gsap.set(b3orb.current, { opacity: 0, scale: 0.4 });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#sb3",
            start: "top top",
            end: "+=80%",
            scrub: 0.75,
          },
        })
        .to(b3orb.current, { opacity: 1, scale: 1, ease: "power2.out" }, 0)
        .to(
          b3word.current,
          {
            letterSpacing: "0.04em",
            opacity: 1,
            scale: 1,
            filter: "blur(0px)",
            ease: "power2.out",
          },
          0,
        )
        .to(b3sub.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.45);

      // ── BEAT 4: final reveal — text wipes up, cyber line fades
      gsap.set(b4line1.current, { y: "110%", opacity: 0 });
      gsap.set(b4line2.current, { y: "110%", opacity: 0 });
      gsap.set(b4cyber.current, { opacity: 0, y: 24 });
      gsap.set(b4rule.current, { width: "0%" });

      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#sb4",
            start: "top top",
            end: "+=80%",
            scrub: 0.65,
          },
        })
        .to(b4rule.current, { width: "100%", ease: "power2.out" }, 0)
        .to(b4line1.current, { y: "0%", opacity: 1, ease: "power3.out" }, 0.05)
        .to(b4line2.current, { y: "0%", opacity: 1, ease: "power3.out" }, 0.12)
        .to(b4cyber.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.45);
    });

    return () => mm.revert();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper>
        {/* ── BEAT 1: NOW LET'S */}
        <Beat id="sb1" h="220vh">
          <Sticky bg="#030008">
            <Grain />
            <ScanLine />
            <Orb
              ref={b1orb}
              s="550px"
              c="rgba(124,58,237,0.25)"
              top="-15%"
              left="-10%"
              op={0.25}
              blur="120px"
            />
            <Rule ref={b1rule} top="52%" />
            <CenterStack>
              <Word
                ref={b1line1}
                size="clamp(5.5rem, 17vw, 17rem)"
                className="stroke"
              >
                LET'S
              </Word>
              <Word ref={b1line2} size="clamp(5.5rem, 17vw, 17rem)">
                TALK
              </Word>
            </CenterStack>
            <Counter>
              01 <span>/</span> 04
            </Counter>
          </Sticky>
        </Beat>

        {/* ── BEAT 2: TALK / ABOUT */}
        {/* <Beat id="sb2" h="220vh">
          <Sticky bg="#040009">
            <Grain />
            <ScanLine />
            <Orb
              s="400px"
              c="rgba(232,121,249,0.18)"
              top="30%"
              right="-8%"
              op={0.2}
              blur="110px"
            />
            <LeftStack>
              <Word ref={b2top} size="clamp(6rem, 20vw, 20rem)">
                TALK
              </Word>
              <Word
                ref={b2bot}
                size="clamp(6rem, 20vw, 20rem)"
                className="stroke"
              >
                ABOUT
              </Word>
            </LeftStack>
            <Counter>
              02 <span>/</span> 04
            </Counter>
          </Sticky>
        </Beat> */}

        {/* ── BEAT 3: SECURITY */}
        <Beat id="sb3" h="240vh">
          <Sticky bg="#050009">
            <Grain />
            <ScanLine />
            <Orb
              ref={b3orb}
              s="700px"
              c="rgba(168,85,247,0.25)"
              top="50%"
              left="50%"
              style={{ transform: "translate(-50%, -50%)" }}
              op={0.22}
              blur="140px"
            />
            <CenterStack style={{ gap: "1.8rem" }}>
              <WordWrap>
                <Word
                  ref={b3word}
                  size="clamp(5rem, 18vw, 18rem)"
                  className="glow flicker"
                >
                  SECURITY
                </Word>
                <GlitchLayer size="clamp(5rem, 18vw, 18rem)">
                  SECURITY
                </GlitchLayer>
              </WordWrap>
              <Whisper ref={b3sub}>
                the kind that actually keeps you safe
              </Whisper>
            </CenterStack>
            <Counter>
              03 <span>/</span> 04
            </Counter>
          </Sticky>
        </Beat>

        {/* ── BEAT 4: NOW LET'S TALK ABOUT + "and I mean" */}
        {/* <Beat id="sb4" h="240vh">
          <Sticky bg="#040008">
            <Grain />
            <ScanLine />
            <Orb
              s="500px"
              c="rgba(124,58,237,0.2)"
              bottom="-10%"
              right="-5%"
              op={0.2}
              blur="120px"
            />
            <Orb
              s="350px"
              c="rgba(232,121,249,0.15)"
              top="-5%"
              left="-5%"
              op={0.15}
              blur="100px"
            />
            <Rule ref={b4rule} top="42%" />
            <CenterStack style={{ gap: "1.2rem" }}>
              <div style={{ overflow: "hidden" }}>
                <Word ref={b4line1} size="clamp(4rem, 13vw, 13rem)">
                  NOW LET'S TALK
                </Word>
              </div>
              <div style={{ overflow: "hidden" }}>
                <Word
                  ref={b4line2}
                  size="clamp(4rem, 13vw, 13rem)"
                  className="glow"
                >
                  ABOUT SECURITY
                  <Cursor />
                </Word>
              </div>
              <CyberLine ref={b4cyber}>
                <CyberDash />
                <CyberText>
                  and I mean — <CyberAccent>Cybersecurity</CyberAccent>
                </CyberText>
                <CyberDash style={{ background: "rgba(168,85,247,0.3)" }} />
              </CyberLine>
            </CenterStack>
            <Counter>
              04 <span>/</span> 04
            </Counter>
          </Sticky>
        </Beat> */}
      </Wrapper>
    </>
  );
}
